import { useState, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react';
import { Button } from 'antd';
import {
  AppstoreOutlined,
  ArrowRightOutlined,
  LogoutOutlined,
  MoonOutlined,
  ReloadOutlined,
  SunOutlined,
  UserOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useKanagawaMode, useKanagawaPalette, useKanagawaToggleMode } from '@aid/theme';
import { APP_STATUS, type AppManifestEntry } from '@aid/shared';
import { useManifest } from './hooks/useManifest';
import { useAuth } from './providers/KeycloakProvider';
import { HeroCardSkeleton, StandardCardSkeleton } from './components/AppCardSkeleton';
import pkg from '../package.json';

/**
 * The manifest schema carries no description/tagline/art fields (only `art`
 * is optional); editorial copy is hardcoded per app id here, falling back
 * to the manifest label for unknown future apps.
 */
const APP_TAGLINES: Record<string, string> = {
  aid: 'Gestión del ISP',
  tepresta: 'Préstamos y empeños',
};

const APP_DESCRIPTIONS: Record<string, string> = {
  aid: 'Clientes, servicios, cobros y pagos del proveedor de internet.',
  tepresta: 'Préstamos y empeños con respaldo y confianza.',
};

const APP_ACCENTS: Record<string, 'blue' | 'green'> = {
  aid: 'blue',
  tepresta: 'green',
};

/**
 * WCAG relative-luminance contrast helper (sRGB linearization with gamma
 * 2.4). Returns white below the luminance threshold, otherwise the palette
 * base. Threshold 0.3 yields the verified pairings: dark blue (#7E9CD8,
 * L≈0.33) and dark green (#98BB6C, L≈0.43) → base; light blue (#4D699B,
 * L≈0.14) and light green (#6F894E, L≈0.22) → white.
 */
function getContrastText(hex: string, base: string): string {
  const channel = (offset: number) => {
    const value = Number.parseInt(hex.slice(offset, offset + 2), 16) / 255;
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  };
  const luminance = 0.2126 * channel(1) + 0.7152 * channel(3) + 0.0722 * channel(5);
  return luminance < 0.3 ? '#FFFFFF' : base;
}

interface AppIconProps {
  app: AppManifestEntry;
  size?: number;
}

/**
 * App monogram with graceful fallback: if the manifest icon fails to load,
 * swap to the label initial in a palette circle. Doubles as the fallback
 * for the card artwork.
 */
function AppIcon({ app, size = 48 }: AppIconProps) {
  const palette = useKanagawaPalette();
  const [broken, setBroken] = useState(false);

  if (broken) {
    return (
      <span
        aria-hidden="true"
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          background: palette.surface0,
          border: `1px solid ${palette.surface1}`,
          color: palette.blue,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: size * 0.46,
          fontWeight: 600,
          fontFamily: 'monospace',
        }}
      >
        {app.label.charAt(0).toUpperCase()}
      </span>
    );
  }

  return (
    <img
      src={app.icon}
      alt=""
      width={size}
      height={size}
      onError={() => setBroken(true)}
      style={{ display: 'block' }}
    />
  );
}

interface AppCardProps {
  app: AppManifestEntry;
  index: number;
  shortcut: number | null;
  disabled?: boolean;
}

/**
 * Notus-style card: 16/10 artwork on top, solid accent block below. The
 * accent block's top edge is cut diagonally (clip-path in hub.css) and
 * overlaps the artwork — a single solid wedge with no seam. Active apps
 * share one structure; disabled apps keep the compact icon + label +
 * "Coming soon" layout.
 */
function AppCard({ app, index, shortcut, disabled = false }: AppCardProps) {
  const palette = useKanagawaPalette();
  const [artBroken, setArtBroken] = useState(false);
  const accent = palette[APP_ACCENTS[app.id] ?? 'blue'];
  const contrastText = getContrastText(accent, palette.base);
  const tagline = APP_TAGLINES[app.id] ?? app.label;
  const description = APP_DESCRIPTIONS[app.id] ?? app.label;

  const cardClass = [
    'hub-card',
    disabled ? 'hub-card--disabled' : '',
    'hub-card-enter',
    index === 1 ? 'hub-enter-card-2' : 'hub-enter-card-1',
  ]
    .filter(Boolean)
    .join(' ');

  const cardStyle: CSSProperties = {
    '--hub-bg': palette.surface0,
    '--hub-border': disabled ? palette.surface2 : palette.surface1,
    '--hub-accent': accent,
    cursor: disabled ? 'not-allowed' : 'pointer',
  } as CSSProperties;

  if (disabled) {
    return (
      <div aria-disabled="true" className={cardClass} style={cardStyle}>
        <AppIcon app={app} size={32} />
        <div className="hub-card__body">
          <span className="hub-card__label">{app.label}</span>
          <span
            className="hub-card__tagline"
            style={{
              color: palette.subtext0,
              fontFamily: 'monospace',
              fontSize: 12,
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: palette.yellow,
              }}
            />
            Coming soon
          </span>
        </div>
      </div>
    );
  }

  return (
    <a
      href={app.url}
      className={cardClass}
      style={cardStyle}
      aria-label={`${app.label}, abrir aplicación`}
      aria-keyshortcuts={shortcut === null ? undefined : String(shortcut)}
    >
      <div className="hub-card__art">
        {app.art && !artBroken ? (
          <img src={app.art} alt="" onError={() => setArtBroken(true)} />
        ) : (
          <div className="hub-card__art-fallback">
            <AppIcon app={app} size={72} />
          </div>
        )}
      </div>
      <div className="hub-card__accent" style={{ background: accent, color: contrastText }}>
        <span className="hub-card__label">{app.label}</span>
        <span className="hub-card__tagline">{tagline}</span>
        <span className="hub-card__desc">{description}</span>
        <span className="hub-cta" aria-hidden="true">
          <ArrowRightOutlined style={{ color: accent }} />
        </span>
      </div>
    </a>
  );
}

export function CatalogPage() {
  const palette = useKanagawaPalette();
  const mode = useKanagawaMode();
  const toggleMode = useKanagawaToggleMode();
  const { logout, userName, initials } = useAuth();
  const { manifest, loading, error } = useManifest();

  const activeApps = manifest?.apps.filter((app) => app.status === APP_STATUS.ACTIVE) ?? [];
  const disabledApps = manifest?.apps.filter((app) => app.status === APP_STATUS.DISABLED) ?? [];

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
    const target = event.target as HTMLElement;
    if (target.closest('input, textarea, select')) return;
    const index = Number.parseInt(event.key, 10) - 1;
    if (Number.isNaN(index) || index < 0 || index >= activeApps.length) return;
    const app = activeApps[index];
    if (!app) return;
    event.preventDefault();
    window.location.assign(app.url);
  };

  let content: ReactNode;
  if (loading) {
    content = (
      <section role="status" aria-label="Loading applications" className="hub-loading">
        <HeroCardSkeleton />
        <StandardCardSkeleton />
      </section>
    );
  } else if (error) {
    content = (
      <div className="hub-state" role="alert">
        <WarningOutlined style={{ fontSize: 40, color: palette.red }} aria-hidden="true" />
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Failed to load applications</h2>
        <p style={{ margin: 0, fontSize: 14, color: palette.subtext0 }}>{error.message}</p>
        <Button
          type="primary"
          icon={<ReloadOutlined />}
          style={{ marginTop: 8 }}
          onClick={() => window.location.reload()}
        >
          Retry
        </Button>
      </div>
    );
  } else if (manifest && manifest.apps.length > 0) {
    content = (
      <>
        <div className="hub-hero hub-card-enter hub-enter-text">
          <h1>Hub de aplicaciones</h1>
          <p>Selecciona la aplicación a la que deseas ingresar</p>
        </div>
        <section className="hub-cards" aria-label="Aplicaciones">
          <div className="hub-grid">
            {activeApps.map((app, index) => (
              <AppCard key={app.id} app={app} index={index} shortcut={index + 1} />
            ))}
            {disabledApps.map((app, index) => (
              <AppCard key={app.id} app={app} index={index} disabled shortcut={null} />
            ))}
          </div>
        </section>
      </>
    );
  } else {
    content = (
      <div className="hub-state">
        <AppstoreOutlined style={{ fontSize: 40, color: palette.subtext0 }} aria-hidden="true" />
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>No applications available</h2>
        <p style={{ margin: 0, fontSize: 14, color: palette.subtext0 }}>
          Contact your administrator to request access.
        </p>
      </div>
    );
  }

  const pageVars = {
    '--hub-blue': palette.blue,
    '--hub-base': palette.base,
    '--hub-text': palette.text,
    '--hub-subtext1': palette.subtext1,
    '--hub-subtext0': palette.subtext0,
    '--hub-surface1': palette.surface1,
  } as CSSProperties;

  return (
    <div
      onKeyDown={handleKeyDown}
      className="hub-page"
      style={{
        background: palette.base,
        color: palette.text,
        ...pageVars,
      }}
    >
      <header
        className="hub-chrome"
        style={{
          background: palette.base,
          borderBottom: `1px solid ${palette.surface1}`,
        }}
      >
        <div className="hub-chrome__inner">
          <div className="hub-chrome__actions">
            <Button
              type="text"
              className="hub-focus"
              aria-label="Toggle color mode"
              icon={mode === 'dark' ? <SunOutlined /> : <MoonOutlined />}
              onClick={toggleMode}
              style={{ color: palette.text }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span
                aria-hidden="true"
                className="hub-avatar"
                style={{ background: palette.surface0, color: palette.blue }}
              >
                {initials ? initials : <UserOutlined style={{ fontSize: 14 }} />}
              </span>
              <span className="hub-user-name" style={{ fontSize: 14, color: palette.subtext1 }}>
                {userName}
              </span>
            </div>
            <Button
              aria-label="Logout"
              className="hub-focus"
              icon={<LogoutOutlined />}
              onClick={logout}
              style={{
                background: 'transparent',
                border: `1px solid ${palette.surface2}`,
                color: palette.text,
              }}
            >
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="hub-main">{content}</main>

      <footer
        className="hub-footer"
        style={{
          background: palette.base,
          borderTop: `1px solid ${palette.surface1}`,
        }}
      >
        v{pkg.version} · hub
      </footer>
    </div>
  );
}
